import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tickets from './pages/Tickets';
import TicketForm from './pages/TicketForm';
import TicketDetail from './pages/TicketDetail';
import Kanban from './pages/Kanban';
import Projects from './pages/Projects';
import WorkItems from './pages/WorkItems';
import Clients from './pages/Clients';
import Users from './pages/Users';
import Organizations from './pages/Organizations';
import AuthCallback from './pages/AuthCallback';
import SettingsPage from './pages/Settings';
import ResourceUtilization from './pages/ResourceUtilization';
import ProjectBilling from './pages/ProjectBilling';
import Approvals from './pages/Approvals';
import ProjectTeam from './pages/ProjectTeam';
import SharedTaskView from './pages/SharedTaskView';
import ResetPassword from './pages/ResetPassword';
import Layout from './components/Layout';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], fullScreen = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  if (fullScreen) {
    return children;
  }

  return <Layout>{children}</Layout>;
};

// Public Route (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();

  // CRITICAL: Detect session_id during render (NOT in useEffect)
  // This synchronous check prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />

      <Route path="/tickets" element={
        <ProtectedRoute>
          <Tickets />
        </ProtectedRoute>
      } />

      <Route path="/tickets/new" element={
        <ProtectedRoute>
          <TicketForm />
        </ProtectedRoute>
      } />

      <Route path="/tickets/:id" element={
        <ProtectedRoute>
          <TicketDetail />
        </ProtectedRoute>
      } />

      <Route path="/kanban" element={
        <ProtectedRoute allowedRoles={['pm', 'developer', 'admin', 'super_admin']}>
          <Kanban />
        </ProtectedRoute>
      } />

      <Route path="/projects" element={
        <ProtectedRoute allowedRoles={['pm', 'developer', 'admin', 'super_admin']}>
          <Projects />
        </ProtectedRoute>
      } />

      <Route path="/work-items" element={
        <ProtectedRoute allowedRoles={['pm', 'developer', 'admin', 'super_admin']}>
          <WorkItems />
        </ProtectedRoute>
      } />

      <Route path="/clients" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
          <Clients />
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin', 'pm']}>
          <Users />
        </ProtectedRoute>
      } />

      <Route path="/organizations" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
          <Organizations />
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin', 'pm']}>
          <SettingsPage />
        </ProtectedRoute>
      } />

      <Route path="/resource-utilization" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin', 'pm']}>
          <ResourceUtilization />
        </ProtectedRoute>
      } />

      <Route path="/project-billing" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin', 'pm']}>
          <ProjectBilling />
        </ProtectedRoute>
      } />

      <Route path="/approvals" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin', 'pm']}>
          <Approvals />
        </ProtectedRoute>
      } />

      <Route path="/project-team" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin', 'pm']}>
          <ProjectTeam />
        </ProtectedRoute>
      } />

      {/* Public shared task view - no auth */}
      <Route path="/shared/:token" element={<SharedTaskView />} />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
